/**
 * SubtitleEditor
 *
 * 句级字幕编辑器（独立路由 /video/edit/subtitles?videoId=xxx）
 *
 * 设计：
 * - 时间轴只显示一份（来自原文 VTT），改时间会同步写到所有译文 VTT
 * - 原文 / 各目标语言译文文本各自独立编辑
 * - 保存通过 PATCH /api/video/subtitles 走单一入口；后端保证两条 VTT 一致
 * - 顶部嵌入播放器；点击任意一行 cue → seek + play；播放中列表高亮当前句并滚动
 * - 时间：波形上拖动边界；列表内仅显示时间。按顺序「下一句 start → 上一句 end」衔接；末句可拖右边界
 * - 每行右上「−」删本句、「+」在本句之前插入（确认后从本句时间中点拆开）；空列表时「添加第一句」
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';
// 直接走 dist 路径以兼容 tsconfig moduleResolution: "node"（不识别 package.json 的 exports）；
// 不写扩展名让 TS 取 .d.ts，webpack 自行解析到 .js
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth } from '../utils/auth';
import { resolveAssetUrl } from '../utils/assetUrl';
import type { ApiResponse, VideoDescriptor } from '../types/api';
import { useHlsPlayer } from '../hooks/useHlsPlayer';
import AppShell from './AppShell';
import './SubtitleEditor.css';

interface SubtitleCue {
  index: number;
  start: number;
  end: number;
  original: string;
  translations: Record<string, string>;
}

interface GetSubtitlesData {
  videoId: string;
  sourceLanguage: string;
  targetLanguages: string[];
  cues: SubtitleCue[];
}

// HH:MM:SS.mmm <-> seconds
function secondsToTimecode(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const totalMs = Math.round(sec * 1000);
  const ms = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

/** 按当前列表顺序：前 n-1 句的 end 一律等于「下一句」的 start（空白并入上一句） */
function rechainCueEndsFromNextStarts(cues: SubtitleCue[]): SubtitleCue[] {
  if (cues.length <= 1) return cues.map(c => ({ ...c }));
  return cues.map((c, i) =>
    i < cues.length - 1 ? { ...c, end: cues[i + 1]!.start } : { ...c },
  );
}

const SubtitleEditor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const videoId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('videoId') || '';
  }, [location.search]);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');

  const [descriptor, setDescriptor] = useState<VideoDescriptor | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState<string>('');
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
  const [cues, setCues] = useState<SubtitleCue[]>([]);
  const [dirty, setDirty] = useState<boolean>(false);

  // 视频当前播放时间（毫秒级显示，由 rAF + timeupdate/seeked 驱动）
  const [currentVideoTime, setCurrentVideoTime] = useState<number>(0);

  // ============== 播放器 ==============
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const hlsUrl = useMemo(() => {
    if (!descriptor) return undefined;
    if (descriptor.hlsStatus === 'ready' && descriptor.assetUrls?.hls?.url) {
      return resolveAssetUrl(descriptor.assetUrls.hls.url);
    }
    return undefined;
  }, [descriptor]);

  const audioUrl = useMemo(() => {
    if (!descriptor) return undefined;
    const u = descriptor.assetUrls?.audio?.url;
    if (!u) return undefined;
    return resolveAssetUrl(u);
  }, [descriptor]);

  // ============== 波形 ==============
  const waveformContainerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const regionsRef = useRef<RegionsPlugin | null>(null);
  const [waveReady, setWaveReady] = useState<boolean>(false);
  const [waveError, setWaveError] = useState<string | null>(null);
  const [waveLoading, setWaveLoading] = useState<boolean>(false);
  const [waveZoom, setWaveZoom] = useState<number>(50); // px per second
  // wavesurfer 回调闭包绑定在初始化时，需要靠 ref 读到最新 cues
  const cuesRef = useRef<SubtitleCue[]>([]);
  useEffect(() => { cuesRef.current = cues; }, [cues]);

  // 根据视频当前时间推导"当前 cue"索引：
  // 1) 优先选 [start, end) 包含当前时间的那一句
  // 2) 否则取"已经开始过"的最近一句（避免播放间隙时跳回 -1）
  const currentCueIndex = useMemo(() => {
    if (cues.length === 0) return -1;
    const t = currentVideoTime;
    for (let i = 0; i < cues.length; i++) {
      const c = cues[i]!;
      if (t >= c.start && t < c.end) return i;
    }
    let last = -1;
    for (let i = 0; i < cues.length; i++) {
      if (cues[i]!.start <= t) last = i; else break;
    }
    return last;
  }, [cues, currentVideoTime]);

  // 把元素滚到 cue-list 滚动容器顶部 + 一点 gap 的位置
  // 由于 cue-list 现在是独立滚动容器，紧挨在播放器 + 波形条下方，
  // 把当前行滚到容器内顶部即可实现"波形 → 当前句"自上而下连贯
  const SCROLL_GAP_BELOW_PLAYER = 16;
  const scrollRowBelowPlayer = useCallback((el: HTMLElement) => {
    const container = el.closest('.cue-list') as HTMLElement | null;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const rowRect = el.getBoundingClientRect();
    const offsetTop = rowRect.top - containerRect.top + container.scrollTop;
    const desired = Math.max(0, offsetTop - SCROLL_GAP_BELOW_PLAYER);
    if (Math.abs(desired - container.scrollTop) < 1) return;
    try {
      container.scrollTo({ top: desired, behavior: 'smooth' });
    } catch {
      container.scrollTop = desired;
    }
  }, []);

  // 视频播到哪儿，下面 cue 列表自动滚到对应行（始终在播放器下方可视区上下居中）
  // 焦点在 textarea/input 时不滚（不抢走正在编辑的视线）
  useEffect(() => {
    if (currentCueIndex < 0) return;
    const ae = document.activeElement as HTMLElement | null;
    if (ae && (ae.tagName === 'TEXTAREA' || ae.tagName === 'INPUT')) return;
    const el = document.getElementById(`cue-row-${currentCueIndex}`);
    if (el) scrollRowBelowPlayer(el);
  }, [currentCueIndex, scrollRowBelowPlayer]);

  const getAuthHeadersStr = useCallback((): Record<string, string> => {
    const h = getAuthHeaders();
    if (h instanceof Headers) {
      const out: Record<string, string> = {};
      h.forEach((v, k) => { out[k] = v; });
      return out;
    }
    if (Array.isArray(h)) {
      return Object.fromEntries(h) as Record<string, string>;
    }
    return h as Record<string, string>;
  }, []);

  const [hlsFatal, setHlsFatal] = useState<Error | null>(null);
  const { videoCallbackRef } = useHlsPlayer({
    enabled: !!hlsUrl,
    hlsUrl,
    getAuthHeaders: getAuthHeadersStr,
    onFatalError: (err) => setHlsFatal(err),
  });

  // 合并 ref：videoRef 用于内部 seek/play，videoCallbackRef 给 useHlsPlayer
  const mergedVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      videoRef.current = el;
      videoCallbackRef(el);
    },
    [videoCallbackRef],
  );

  // 监听 timeupdate / seeked 仅用于更新毫秒级时间显示
  // 注意：<video> 元素在 hlsUrl 就绪后才挂载，所以依赖 hlsUrl 让 effect 在挂载后重新执行
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setCurrentVideoTime(v.currentTime);
    };
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('seeked', onTime);
    v.addEventListener('loadedmetadata', onTime);
    onTime();
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('seeked', onTime);
      v.removeEventListener('loadedmetadata', onTime);
    };
  }, [hlsUrl]);

  // 播放中用 rAF 把时间显示推进到毫秒级（timeupdate 只 ~250ms 一次，看上去会"卡"）
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let rafId: number | null = null;
    const tick = () => {
      if (!v.paused && !v.ended) {
        const t = v.currentTime;
        setCurrentVideoTime(prev => (Math.abs(prev - t) < 0.005 ? prev : t));
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };
    const onPlay = () => {
      if (rafId == null) rafId = requestAnimationFrame(tick);
    };
    const onPauseEnded = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      setCurrentVideoTime(v.currentTime);
    };
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPauseEnded);
    v.addEventListener('ended', onPauseEnded);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPauseEnded);
      v.removeEventListener('ended', onPauseEnded);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [hlsUrl]);

  // ============== 波形：初始化 / 销毁 ==============
  // audio.wav 走鉴权接口，先用 auth 头取回 blob，再喂给 wavesurfer
  useEffect(() => {
    if (!audioUrl) return;
    const container = waveformContainerRef.current;
    if (!container) return;

    let cancelled = false;
    let ws: WaveSurfer | null = null;
    let blobUrl: string | null = null;

    setWaveReady(false);
    setWaveError(null);
    setWaveLoading(true);

    (async () => {
      try {
        const headers = getAuthHeaders();
        const resp = await fetch(audioUrl, { headers: headers as HeadersInit });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);

        const regions = RegionsPlugin.create();
        regionsRef.current = regions;

        ws = WaveSurfer.create({
          container,
          waveColor: '#9aa6c7',
          progressColor: '#667eea',
          cursorColor: '#ff5252',
          cursorWidth: 2,
          height: 80,
          normalize: true,
          interact: true, // 允许点击波形定位；其内部 <audio> 我们不会 play()，不会出声
          url: blobUrl,
          minPxPerSec: 50,
          plugins: [regions],
        });
        wsRef.current = ws;

        ws.on('ready', () => {
          if (cancelled) return;
          setWaveReady(true);
          setWaveLoading(false);
          try { ws!.zoom(waveZoom); } catch { /* noop */ }
        });

        // 用户点击波形 → wavesurfer 已把内部光标移到该时间，这里再把视频跟过去
        ws.on('interaction', (newTime: number) => {
          if (!Number.isFinite(newTime)) return;
          const v = videoRef.current;
          if (v) {
            try { v.currentTime = newTime; } catch { /* noop */ }
          }
        });

        // 点击 cue 的高亮区间 → 跳到该 cue 的 start 并播放
        regions.on('region-clicked', (region: Region, e: MouseEvent) => {
          e.stopPropagation();
          const v = videoRef.current;
          if (!v) return;
          try {
            v.currentTime = Math.max(0, region.start);
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => undefined);
          } catch { /* noop */ }
        });

        // 拖动 region 边界 → 同步到 cues
        // 仅在 mouseup（region-updated）提交，避免拖动过程中频繁 setState 触发重建
        regions.on('region-updated', (region: Region, side?: 'start' | 'end') => {
          const m = /^cue-(\d+)$/.exec(region.id);
          if (!m) return;
          const cueIndex = parseInt(m[1]!, 10);
          const prevCues = cuesRef.current;
          const idxInList = prevCues.findIndex(c => c.index === cueIndex);
          if (idxInList < 0) return;

          const newCues = prevCues.map(c => ({ ...c }));
          const EPS = 0.05; // 最小间隔 50ms
          if (side === 'start') {
            const minStart = idxInList > 0 ? newCues[idxInList - 1]!.start + EPS : 0;
            const maxStart = newCues[idxInList]!.end - EPS;
            const clamped = Math.max(minStart, Math.min(maxStart, region.start));
            newCues[idxInList]!.start = clamped;
            // 链式：上一句 end = 当前 start
            if (idxInList > 0) newCues[idxInList - 1]!.end = clamped;
          } else if (side === 'end') {
            // 只允许最后一句独立改 end
            if (idxInList !== newCues.length - 1) return;
            const minEnd = newCues[idxInList]!.start + EPS;
            newCues[idxInList]!.end = Math.max(minEnd, region.end);
          } else {
            return;
          }

          setCues(newCues);
          setDirty(true);
        });

        ws.on('error', (err: Error) => {
          if (cancelled) return;
          setWaveError(err?.message || '波形加载失败');
          setWaveLoading(false);
        });
      } catch (e) {
        if (cancelled) return;
        setWaveError(e instanceof Error ? e.message : String(e));
        setWaveLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      try { ws?.destroy(); } catch { /* noop */ }
      wsRef.current = null;
      regionsRef.current = null;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setWaveReady(false);
    };
    // waveZoom 故意不放依赖：仅作为初始 zoom；后续靠 zoom effect 单独同步
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  // 波形指针同步到视频当前时间
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || !waveReady) return;
    try { ws.setTime(currentVideoTime); } catch { /* noop */ }
  }, [currentVideoTime, waveReady]);

  // 应用缩放
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || !waveReady) return;
    try { ws.zoom(waveZoom); } catch { /* noop */ }
  }, [waveZoom, waveReady]);

  // 把每条 cue 的 [start, end] 画成 region 色块
  // - 左边（start）可拖：拖完更新本句 start + 上一句 end（链式）
  // - 右边（end）只在最后一句可拖；非末句的 end 与下一句的 start 共享同一个把手（拖下一句的左边即可）
  // cues state 只在边界提交时变更（非按键级），重建成本可控
  useEffect(() => {
    const rg = regionsRef.current;
    if (!rg || !waveReady) return;
    try { rg.clearRegions(); } catch { /* noop */ }
    cues.forEach((c, i) => {
      const isLast = i === cues.length - 1;
      const start = Number.isFinite(c.start) ? c.start : 0;
      const endRaw = Number.isFinite(c.end) ? c.end : start;
      const end = endRaw > start ? endRaw : start + 0.05;
      try {
        const region = rg.addRegion({
          id: `cue-${c.index}`,
          start,
          end,
          color: i % 2 === 0 ? 'rgba(102, 126, 234, 0.16)' : 'rgba(45, 212, 191, 0.16)',
          drag: false,
          resize: true,
          resizeStart: true,
          resizeEnd: isLast,
          minLength: 0.05,
          content: `#${i + 1}`,
        });
        // wavesurfer v7 在 resize:true 时无条件渲染左右两个把手，resizeEnd:false 只是不提交拖动事件
        // 这会导致两句相邻的边界出现两个重叠把手（一个还不工作），所以非末句把右把手元素直接摘掉
        if (!isLast && region?.element) {
          const rh = region.element.querySelector('[part~="region-handle-right"]');
          rh?.remove();
        }
      } catch { /* noop：长度无效等忽略 */ }
    });
  }, [cues, waveReady]);

  // ============== 加载字幕 + descriptor ==============
  useEffect(() => {
    if (!videoId) {
      navigate('/profile');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrorMsg('');
      try {
        // 并发拉字幕 + descriptor
        const [subResp, metaResp] = await Promise.all([
          fetch(`${API_BASE_URL}/video/subtitles?videoId=${encodeURIComponent(videoId)}`, {
            headers: getAuthHeaders(),
          }),
          fetch(`${API_BASE_URL}/video/metadata?videoId=${encodeURIComponent(videoId)}`, {
            headers: getAuthHeaders(),
          }),
        ]);
        if (subResp.status === 401 || metaResp.status === 401) {
          clearAuth();
          navigate('/login');
          return;
        }
        const subData: ApiResponse<GetSubtitlesData> = await subResp.json();
        const metaData: ApiResponse<VideoDescriptor> = await metaResp.json();
        if (cancelled) return;
        if (!subData.success || !subData.data) {
          setErrorMsg(subData.error || '加载字幕失败');
          return;
        }
        setSourceLanguage(subData.data.sourceLanguage);
        setTargetLanguages(subData.data.targetLanguages);
        const chained = rechainCueEndsFromNextStarts(subData.data.cues.map(c => ({ ...c })));
        setCues(chained);
        setDirty(false);
        if (metaData.success && metaData.data) {
          setDescriptor(metaData.data);
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [videoId, navigate]);

  // 离开页面提醒
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ============== 编辑动作 ==============
  const updateOriginal = (idx: number, value: string) => {
    setCues(prev => prev.map(c => (c.index === idx ? { ...c, original: value } : c)));
    setDirty(true);
  };

  const updateTranslation = (idx: number, lang: string, value: string) => {
    setCues(prev =>
      prev.map(c =>
        c.index === idx ? { ...c, translations: { ...c.translations, [lang]: value } } : c,
      ),
    );
    setDirty(true);
  };

  // 点击行：seek + play
  const handleJumpToCue = (cue: SubtitleCue) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.currentTime = Math.max(0, cue.start);
    } catch {
      // 某些状态下设置 currentTime 会抛错，吞掉
    }
    const playPromise = v.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => undefined);
    }
  };

  // 标记「刚刚新增的句子 index」，副作用里用它做 scroll + 自动聚焦原文 textarea
  const [newlyAddedId, setNewlyAddedId] = useState<number | null>(null);

  /** 空列表时添加第一句（固定时间，与播放头无关） */
  const handleAddFirstCue = () => {
    const ok = window.confirm(
      '将添加第一条空白字幕（约 2 秒时长）。\n确定后可在上方波形上拖动边界微调。',
    );
    if (!ok) return;
    const DEFAULT_DUR = 2;
    const nextId = 0;
    const newCue: SubtitleCue = {
      index: nextId,
      start: 0,
      end: DEFAULT_DUR,
      original: '',
      translations: Object.fromEntries(targetLanguages.map(lang => [lang, ''])),
    };
    setCues([newCue]);
    setDirty(true);
    setNewlyAddedId(nextId);
    const v = videoRef.current;
    try {
      if (v) v.currentTime = 0;
    } catch { /* noop */ }
  };

  /**
   * 在本句之前插入一句空白字幕：从当前行时间区间的中点拆开，
   * 前半段给新句，后半段仍保留本行原文与各译文（仅时间变短，可在波形上再调）。
   */
  const handleAddCueBefore = (cueId: number) => {
    const prev = cuesRef.current;
    const pos = prev.findIndex(c => c.index === cueId);
    if (pos < 0) return;
    const target = prev[pos]!;
    const span = target.end - target.start;
    const EPS = 0.05;
    const MIN_SPLIT = EPS * 4; // 两句各至少约 100ms
    if (span < MIN_SPLIT) {
      window.alert('本句时间太短，无法从中拆开插入。请先在波形上把本句区间拉长一些。');
      return;
    }
    const ok = window.confirm(
      `将在第 ${pos + 1} 句（本行）之前插入一句空白字幕。\n` +
        '当前句的时间会从中间拆开：前半段给新句，后半段仍用于本句的原文与译文（之后可在波形上微调）。\n\n确定吗？',
    );
    if (!ok) return;

    const split = (target.start + target.end) / 2;
    const nextId = Math.max(...prev.map(c => c.index), -1) + 1;
    const newCue: SubtitleCue = {
      index: nextId,
      start: target.start,
      end: split,
      original: '',
      translations: Object.fromEntries(targetLanguages.map(lang => [lang, ''])),
    };
    const shortened: SubtitleCue = { ...target, start: split };
    const result = [...prev.slice(0, pos), newCue, shortened, ...prev.slice(pos + 1)];
    const chained = rechainCueEndsFromNextStarts(result);

    const v = videoRef.current;
    try {
      const created = chained.find(c => c.index === nextId);
      if (v && created) v.currentTime = Math.max(0, created.start);
    } catch { /* noop */ }

    setCues(chained);
    setDirty(true);
    setNewlyAddedId(nextId);
  };

  // 新增后：滚到新句并聚焦原文输入
  useEffect(() => {
    if (newlyAddedId == null) return;
    if (!cues.some(c => c.index === newlyAddedId)) return;
    const row = document.querySelector(`[data-cue-id="${newlyAddedId}"]`) as HTMLElement | null;
    if (row) {
      scrollRowBelowPlayer(row);
      const ta = row.querySelector('textarea') as HTMLTextAreaElement | null;
      if (ta) {
        // preventScroll: true 避免浏览器默认聚焦时再做一次 scroll，跟我们上面的 smooth scroll 撞
        try { ta.focus({ preventScroll: true }); } catch { /* noop */ }
      }
    }
    setNewlyAddedId(null);
  }, [newlyAddedId, cues, scrollRowBelowPlayer]);

  // 删除一句字幕（原文 + 所有译文 + 该时间段，整条一起删）
  // 删完用 rechain 重链：上一句的 end 自动顺延到下一句的 start
  const handleDeleteCue = (cueId: number) => {
    const pos = cues.findIndex(c => c.index === cueId);
    if (pos < 0) return;
    const cue = cues[pos]!;
    const preview = cue.original.trim().slice(0, 40);
    const ok = window.confirm(
      `确定删除第 ${pos + 1} 句吗？\n` +
      (preview ? `「${preview}${cue.original.length > 40 ? '…' : ''}」\n` : '') +
      `（所有语言的对应行都会删掉）`,
    );
    if (!ok) return;

    setCues(prev => {
      const next = prev.filter(c => c.index !== cueId);
      return rechainCueEndsFromNextStarts(next);
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSuccessMsg('');
    setErrorMsg('');

    const sorted = [...cues].sort((a, b) => a.start - b.start || a.index - b.index);
    const toSave = rechainCueEndsFromNextStarts(sorted.map(c => ({ ...c })));

    for (let i = 0; i < toSave.length; i++) {
      const c = toSave[i]!;
      if (!Number.isFinite(c.start) || !Number.isFinite(c.end) || c.start < 0 || c.end <= c.start) {
        setErrorMsg(`第 ${i + 1} 句时间无效：${secondsToTimecode(c.start)} → ${secondsToTimecode(c.end)}`);
        return;
      }
    }

    setSaving(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/video/subtitles`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId,
          cues: toSave.map(c => ({
            start: c.start,
            end: c.end,
            original: c.original,
            translations: c.translations,
          })),
        }),
      });
      if (resp.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const data: ApiResponse<{ count: number }> = await resp.json();
      if (!data.success) {
        setErrorMsg(data.error || '保存失败');
        return;
      }
      const reindexed: SubtitleCue[] = toSave.map((c, i) => ({ ...c, index: i }));
      setCues(reindexed);
      setDirty(false);
      setSuccessMsg(`保存成功（${data.data?.count ?? toSave.length} 句）`);
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (dirty) {
      const ok = window.confirm('有未保存的修改，确定离开吗？');
      if (!ok) return;
    }
    navigate(`/video/edit?videoId=${encodeURIComponent(videoId)}`);
  };

  const header = (
    <div className="subtitle-editor-header app-shell-header-content">
      <button onClick={handleBack} className="back-button" title="返回">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>
      <h1>编辑字幕</h1>
      <div className="subtitle-editor-header-actions">
        <button
          className="save-button-inline"
          onClick={handleSave}
          disabled={saving || !dirty || loading}
        >
          {saving ? '保存中…' : dirty ? '保存' : '已保存'}
        </button>
      </div>
    </div>
  );

  return (
    <AppShell header={header} footer={null} bodyScrollable={false}>
      <div className="subtitle-editor-container">
        {loading ? (
          <div className="loading-message">加载中…</div>
        ) : (
          <>
            {/* 顶部嵌入式播放器（粘性） */}
            <div className="player-section">
              {hlsUrl ? (
                <video
                  ref={mergedVideoRef}
                  className="editor-video"
                  controls
                  playsInline
                  preload="metadata"
                />
              ) : (
                <div className="player-placeholder">
                  {descriptor && descriptor.hlsStatus !== 'ready'
                    ? '视频流尚未准备好（HLS 未就绪）'
                    : '没有可用的视频源'}
                </div>
              )}
              {hlsUrl && (
                <div className="player-time-bar">
                  <span className="player-time-label">当前时间</span>
                  <span className="player-time-value">{secondsToTimecode(currentVideoTime)}</span>
                </div>
              )}

              {audioUrl && (
                <div className="waveform-section">
                  <div className="waveform-toolbar">
                    <span className="waveform-toolbar-label">波形</span>
                    <span className="waveform-toolbar-hint">
                      {waveLoading ? '加载中…' : waveError ? `加载失败：${waveError}` : '点击波形可跳转到该位置'}
                    </span>
                    <span className="waveform-zoom">
                      <span className="waveform-zoom-label">缩放</span>
                      <input
                        type="range"
                        min={20}
                        max={400}
                        step={10}
                        value={waveZoom}
                        onChange={e => setWaveZoom(parseInt(e.target.value, 10))}
                        disabled={!waveReady}
                        className="waveform-zoom-range"
                      />
                      <span className="waveform-zoom-value">{waveZoom}px/s</span>
                    </span>
                  </div>
                  <div className="waveform-scroll">
                    <div ref={waveformContainerRef} className="waveform-container" />
                  </div>
                </div>
              )}
              {hlsFatal && (
                <div className="error-banner">播放器错误：{hlsFatal.message}</div>
              )}
            </div>

            {errorMsg && <div className="error-banner">{errorMsg}</div>}
            {successMsg && <div className="success-banner">{successMsg}</div>}

            {cues.length > 0 && (
              <div className="cue-list-count-bar">共 {cues.length} 句</div>
            )}

            <div className="cue-list">
              {cues.map((cue, i) => {
                return (
                  <div
                    id={`cue-row-${i}`}
                    data-cue-id={cue.index}
                    key={cue.index}
                    className={`cue-row${currentCueIndex === i ? ' is-active' : ''}`}
                  >
                    <div className="cue-row-head">
                      <button
                        type="button"
                        className="cue-jump-btn"
                        title="跳转到此句并播放"
                        onClick={() => handleJumpToCue(cue)}
                      >
                        ▶
                      </button>
                      <span className="cue-index">#{i + 1}</span>
                      <span className="time-readonly" title="开始时间，由波形拖动调整">
                        {secondsToTimecode(cue.start)}
                      </span>
                      <span className="time-arrow">→</span>
                      <span
                        className="time-readonly"
                        title={i === cues.length - 1 ? '结束时间，由波形拖动调整' : '自动等于下一句的开始时间'}
                      >
                        {secondsToTimecode(cue.end)}
                      </span>
                      <span className="cue-row-actions" title="删除本句 / 在本句之前插入">
                        <button
                          type="button"
                          className="cue-row-action cue-minus"
                          title="删除此句（原文 + 所有译文）"
                          onClick={() => handleDeleteCue(cue.index)}
                        >
                          −
                        </button>
                        <button
                          type="button"
                          className="cue-row-action cue-plus"
                          title="在本句之前插入一句空白字幕"
                          onClick={() => handleAddCueBefore(cue.index)}
                        >
                          +
                        </button>
                      </span>
                    </div>
                    <div className="cue-row-body">
                      <div className="text-col">
                        <label className="text-label">{sourceLanguage}</label>
                        <textarea
                          className="text-area"
                          value={cue.original}
                          rows={2}
                          onChange={e => updateOriginal(cue.index, e.target.value)}
                        />
                      </div>
                      {targetLanguages.map(lang => (
                        <div className="text-col" key={lang}>
                          <label className="text-label">{lang}</label>
                          <textarea
                            className="text-area"
                            value={cue.translations[lang] ?? ''}
                            rows={2}
                            onChange={e => updateTranslation(cue.index, lang, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {cues.length === 0 && !loading && (
                <div className="empty-tip">
                  没有可编辑的字幕。
                  <button
                    type="button"
                    className="cue-add-first-btn"
                    onClick={handleAddFirstCue}
                  >
                    ＋ 添加第一句
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
};

export default SubtitleEditor;
